import http from '@/http'
import { ActionTree } from 'vuex'
import { RootState } from '@/store/types'
import { Actions, PharmaciesState } from '@/store/pharmacies/types'
import { addDays, addMonths, endOfMonth, format } from 'date-fns'
import { AxiosError } from 'axios'
import { ErrorResponseData } from '@/types'
import { isDevHost } from '@/utils/host'
import { showRequestErrorToast, showRequestErrorToastNoClose } from '@/utils/toast'
import router from '@/router'
import store from '@/store'

const handleServerError = () => {
  localStorage.removeItem('pharmId')
  localStorage.removeItem('token')
  isDevHost() ? router?.push(`/authentication-test`) : router?.push(`/authentication`)
}

const adminShiftTypes = ['Main', 'Partner', 'Exchange', 'ProfiPst', 'MainProfi']
const adminShiftStatuses = ['New', 'Active', 'Done', 'Miss', 'Canceled', 'MissAccepted']

const actions: ActionTree<PharmaciesState, RootState> & Actions = {
  async getPharmacies({ commit, state, rootState }) {
    try {
      if (Number(localStorage.getItem('pharmSkip')) > 0) {
        commit('setPharmSkip', Number(localStorage.getItem('pharmSkip')))
      }
      let queryStr = ''

      if (localStorage.getItem('pharmSearch')) {
        const queryTempStr = localStorage.getItem('pharmSearch') || ''
        queryStr = `filter%5BsearchQuery%5D=${encodeURIComponent(queryTempStr)}&`
      }

      let pharmsUrl = `/api/pharms/offline-admin?${queryStr}skip=${state.pharms_skip}&take=${state.pharms_take}`
      if (rootState.extendedRole || rootState.gurvRole || rootState.wfmReaderRole) {
        pharmsUrl = `/api/pharms/gurv?${queryStr}skip=${state.pharms_skip}&take=${state.pharms_take}`
      }
      const { data } = await http.get(pharmsUrl)
      commit('setPharmaciesId', data.data)
      commit('setTomorrowWorkTime')
      commit('setPharmList', data)
      commit('setPharmCount', data)

      if (!localStorage.getItem('pharmId')) {
        localStorage.setItem('pharmId', data.data[0].id)
      }

      if (data.data[0].id !== Number(localStorage.getItem('pharmId'))) {
        commit('setUsers', Number(localStorage.getItem('pharmId')))
        commit('setProfiPstUsers', Number(localStorage.getItem('pharmId')))
      } else {
        commit('setUsers', data.data[0].id)
        commit('setProfiPstUsers', data.data[0].id)
      }

      if (data.data[0].pharmTz) {
        commit('setPharmTz', data.data[0].pharmTz)
      }
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (localStorage.getItem('token') && error.response?.status === 503) {
        handleServerError()
        commit('closeErrorDialog', error.response.data.message)
        rootState.axiosController.abort()
      }
    }
  },
  async getExternalUsers({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return

      const params = new URLSearchParams()
      params.append('filter[startDate]', rootState.startPeriod)
      params.append('filter[stopDate]', rootState.endPeriod)

      adminShiftTypes.forEach((type) => params.append('filter[shiftType]', type))
      adminShiftStatuses.forEach((status) => params.append('filter[shiftStatus]', status))

      params.append('filter[aptId]', state.pharmId.toString())
      params.append('take', '10000')

      const reqUrl = `${rootState.url}api/admin-shift?${params.toString()}`
      const { data } = await http.get(reqUrl)

      if (Array.isArray(data.data) && data.data.length > 0) {
        commit('setAdminShiftBuffer', data.data) // Сохраняем смены для заполнения в getAdminShift (OMR-4601)
        commit('setExternalUsers', data.data)
      } else {
        commit('setAdminShiftBuffer', [])
        commit('setExternalUsers', null)
      }
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (localStorage.getItem('token') && error.response?.status === 503) {
        handleServerError()
        commit('closeErrorDialog', error.response.data.message)
        rootState.axiosController.abort()
      }
    }
  },
  async getPharmsSearchList({ commit, state, rootState }, query) {
    // Получение списка аптек при поиске
    try {
      let queryStr = ''
      if (query || state.pharms_search) {
        queryStr = `filter%5BsearchQuery%5D=${encodeURIComponent(state.pharms_search)}&`
      }
      let pharmsUrl = `/api/pharms/offline-admin?${queryStr}skip=${state.pharms_skip}&take=${state.pharms_take}`
      if (rootState.extendedRole || rootState.gurvRole || rootState.wfmReaderRole) {
        pharmsUrl = `/api/pharms/gurv?${queryStr}skip=${state.pharms_skip}&take=${state.pharms_take}`
      }
      const { data } = await http.get(pharmsUrl)
      commit('setPharmList', data)
      if (data.meta && data.meta.count > 0) {
        commit('setPharmCount', data)
      }
    } catch {
      if (localStorage.getItem('token')) console.error('Ошибка получения списка')
    }
  },
  async getFondWorkTime({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const { data } = await http.get(
        `${rootState.url}api/frv/pharm/${state.pharmId}?date=${rootState.startPeriod}`,
      )
      commit('setFondWorkTime', data)
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      commit('resetFondWorkTime')
      if (error.response && localStorage.getItem('token'))
        showRequestErrorToastNoClose(error, 'Не удалось получить данные по ФРВ аптеки')
    }
  },
  // ФРВ для модалки создания потребности Беру (date — первый день следующего месяца, если потребность в нём)
  async getBeruFondWorkTime({ commit, state, rootState }, date?) {
    if (!state.pharmId) return
    const { data } = await http.get(
      `${rootState.url}api/frv/pharm/${state.pharmId}?date=${date || rootState.startPeriod}`,
    )
    commit('setBeruFondWorkTime', data)
  },
  async getTimeSchedule({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const endPeriod = format(addDays(new Date(rootState.endPeriod), -1), 'yyyy-M-d')
      const { data } = await http.get(
        `/api/pharms/pharm-wtime-schedule/${state.pharmId}?filter%5BstartDate%5D=${rootState.startPeriod}&filter%5BstopDate%5D=${endPeriod}`,
      )
      commit('setTimeSchedule', data.data)
    } catch {
      commit('setTimeSchedule', {})
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async getAdminShift({ commit, state, rootState }, shiftsLoaded = false) {
    try {
      if (!state.pharmId) return

      if (shiftsLoaded && state.admin_shift_buffer) {
        // Если запрос уже был выполнен, то заполняем смены из него (OMR-4601)
        commit('setAdminShift', state.admin_shift_buffer)
      } else {
        // В остальных случаях делаем запрос как обычно
        const params = new URLSearchParams()
        params.append('filter[startDate]', rootState.startPeriod)
        params.append('filter[stopDate]', rootState.endPeriod)

        adminShiftTypes.forEach((type) => params.append('filter[shiftType]', type))
        adminShiftStatuses.forEach((status) => params.append('filter[shiftStatus]', status))

        params.append('filter[aptId]', state.pharmId.toString())
        params.append('take', '10000')

        const reqUrl = `${rootState.url}api/admin-shift?${params.toString()}`
        const { data } = await http.get(reqUrl)
        commit('setAdminShift', data.data)
      }
    } catch {
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async getPharmSchedule({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const { data } = await http.get(
        `api/pharms/pharm-schedule/${state.pharmId}?filter%5BstartDate%5D=${rootState.startPeriod}&filter%5BstopDate%5D=${rootState.endPeriod}`,
      )
      commit('setPharmSchedule', data.data)
    } catch {
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async getFondWorkTimeHuman({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const { data } = await http.get(
        `${rootState.url}api/frv/human/pharm?date=${rootState.startPeriod}&pharmId=${state.pharmId}`,
      )
      commit('setFondWorkTimeHuman', data.data)
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      // Сброс ФРВ при ошибке получения с сервера
      commit('resetFondWorkTimeHuman')
      if (error.response && localStorage.getItem('token'))
        showRequestErrorToastNoClose(error, 'Не удалось получить данные по ФРВ сотрудников')
    }
  },
  async getHolidays({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const { data } = await http.get(
        `/api/holidays?filter%5BstartDate%5D=${rootState.startPeriod}&filter%5BstopDate%5D=${rootState.endPeriod}&filter%5BpharmId%5D=${state.pharmId}`,
      )
      commit('setHolidays', data.holidays)
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.response && localStorage.getItem('token'))
        showRequestErrorToastNoClose(error, 'Не удалось получить данные о праздничных днях')
    }
  },
  // Праздники для модалки создания потребности Беру (date — первый день следующего месяца, если потребность в нём)
  async getBeruHolidays({ commit, state, rootState }, date?) {
    if (!state.pharmId) return
    const startDate = date || rootState.startPeriod
    const stopDate = date
      ? format(addDays(endOfMonth(new Date(date)), 1), 'yyyy-MM-dd')
      : rootState.endPeriod
    const { data } = await http.get(
      `/api/holidays?filter%5BstartDate%5D=${startDate}&filter%5BstopDate%5D=${stopDate}&filter%5BpharmId%5D=${state.pharmId}`,
    )
    commit('setBeruHolidays', data.holidays)
  },
  async getShiftsExistence({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const startPeriod = format(addMonths(new Date(rootState.startPeriod), 1), 'yyyy-M-d')
      const endPeriod = format(addMonths(new Date(rootState.endPeriod), 1), 'yyyy-M-d')
      const { data } = await http.get(
        `${rootState.url}api/frv/existence?filter%5BdateFrom%5D=${startPeriod}&filter%5BdateTo%5D=${endPeriod}&filter%5BpharmId%5D=${state.pharmId}`,
      )
      commit('setShiftsExistence', data)
    } catch {
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async getTimeScheduleDetails({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const endPeriod = format(addDays(new Date(rootState.endPeriod), -1), 'yyyy-M-d')
      const { data } = await http.get(
        `${rootState.url}api/admin-shift/pharm-dayfull/${state.pharmId}?filter%5BstartDate%5D=${rootState.startPeriod}&filter%5BstopDate%5D=${endPeriod}`,
      )
      commit('setTimeScheduleDetails', data.data)
    } catch {
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async getPastTimeScheduleDetails({ commit, state, rootState }) {
    try {
      if (!state.pharmId) return
      const startPeriod = format(addMonths(new Date(rootState.startPeriod), 1), 'yyyy-M-d')
      const endPeriod = format(
        endOfMonth(addMonths(new Date(rootState.startPeriod), 1)),
        'yyyy-M-d',
      )
      const { data } = await http.get(
        `${rootState.url}api/admin-shift/pharm-dayfull/${state.pharmId}?filter%5BstartDate%5D=${startPeriod}&filter%5BstopDate%5D=${endPeriod}`,
      )
      commit('setPastTimeScheduleDetails', data.data)
    } catch {
      // if (e.response) commit('closeErrorDialog', e.response.data.message)
    }
  },
  async deleteShift({ commit, rootState }, itemShift) {
    try {
      const data = await http.delete(`${rootState.url}api/admin-shift/${itemShift.id}`)
      commit('deleteUserShift', itemShift)
      return data.status
    } catch (e) {
      throw e as AxiosError<ErrorResponseData>
    }
  },
  async updateShift({ rootState }, itemShift) {
    try {
      const { data } = await http.put(`${rootState.url}api/admin-shift/${itemShift.id}`, itemShift)
      return data
    } catch (e) {
      throw e as AxiosError<ErrorResponseData>
    }
  },
  async updateMissShift({ commit, rootState }, data) {
    try {
      if (!data.shift.id) return
      await http.patch(
        `${rootState.url}api/admin-shift/${data.accepted ? 'accept-miss' : 'reject-miss'}/${data.shift.id}`,
      )
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.response && localStorage.getItem('token'))
        commit('closeErrorDialog', error.response.data.message)
    }
  },
  async getFRVReport({ commit, rootState }, startDate) {
    try {
      if (!startDate) return
      const filterDate = format(new Date(startDate), 'yyyy-MM-d')
      const { data } = await http.get(
        `${rootState.url}api/frv/report?filter%5BdateFrom%5D=${filterDate}&filter%5BdateTo%5D=${filterDate}`,
      )
      return data.location
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.response && localStorage.getItem('token'))
        commit('closeErrorDialog', error.response.data.message)
    }
  },
  async addShift({ rootState }, itemShift) {
    try {
      const { data } = await http.post(`${rootState.url}api/admin-shift`, { shifts: [itemShift] })
      return data
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.response && localStorage.getItem('token')) showRequestErrorToast(error)
      return error.response?.data
    }
  },
  async getPharmPeopleList({ commit, state }, query) {
    try {
      let queryStr = ''
      if (query) {
        queryStr = `filter%5BsearchQuery%5D=${encodeURIComponent(query)}&`
      }
      const pharmsUrl = `/api/admin-user/web-subjectrf-users?${queryStr}filter%5BpharmId%5D=${state.pharmId}&skip=${state.search_user_skip}&take=${state.search_user_take}`

      const { data } = await http.get(pharmsUrl)

      commit('setUserSearchList', data.data)
      if (data.meta) {
        commit('setUserSearchSkip', data.meta.skip)
        commit('setUserSearchCount', data.meta.count)
      }
    } catch {
      if (localStorage.getItem('token')) console.error('Ошибка получения пользователей')
    }
  },
  async getShiftInfo({ commit, rootState }, shiftId) {
    try {
      commit('clearShiftInfo') // Очистка прошлой информации о смене
      if (!shiftId) return
      const { data } = await http.get(
        `${rootState.url}api/admin-shift/web-shift-history/${shiftId}`,
      )

      if (data && data.shiftId) {
        commit('setShiftInfo', data)
      }
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      console.error(error)
      // if (error.response && localStorage.getItem('token')) commit('closeErrorDialog', error.response.data.message)
    }
  },
  async addVacation({ commit }, itemVacation) {
    try {
      const { data } = await http.post(`/api/vacations`, itemVacation)
      return data
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.status === 500 && localStorage.getItem('token')) {
        store.commit('data/closeErrorDialog', 'Ошибка загрузки данных')
      } else if (error.response && localStorage.getItem('token')) {
        commit('closeErrorDialog', error.response.data.message)
      }
      return error.status
    }
  },
  async updateVacation({ commit }, vacation) {
    try {
      if (!vacation.pharmId || !vacation.zupGuid) return
      const { id, pharmId, zupGuid, dateFrom, dateTo } = vacation
      const { data } = await http.patch(`/api/vacations/${id}/${zupGuid}`, {
        pharmId,
        dateFrom,
        dateTo,
      })
      return data
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.status === 500 && localStorage.getItem('token')) {
        store.commit('data/closeErrorDialog', 'Ошибка загрузки данных')
      } else if (error.response && localStorage.getItem('token')) {
        commit('closeErrorDialog', error.response.data.message)
      }
      return error.status
    }
  },
  async cancelVacation({ commit }, vacation) {
    try {
      if (!vacation.id || !vacation.zupGuid) return
      const { data } = await http.delete(`/api/vacations/${vacation.id}/${vacation.zupGuid}`)
      return data
    } catch (e) {
      const error = e as AxiosError<ErrorResponseData>
      if (error.status === 500 && localStorage.getItem('token')) {
        store.commit('data/closeErrorDialog', 'Ошибка загрузки данных')
      } else if (error.response && localStorage.getItem('token')) {
        commit('closeErrorDialog', error.response.data.message)
      }
      return error.status
    }
  },
}

export default actions
